import { legacyTicketRedirect } from './utils/legacyTicketRedirect'
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { useEffect, lazy, Suspense } from 'react'
import { useAppSettingsStore } from './stores/appSettingsStore'
import { useAuthStore } from './stores/authStore'
import { authApi } from './utils/api'
import { findThemePreset, type ThemePresetId } from './theme/themePresets'
import { useSessionTimeout } from './hooks/useSessionTimeout'
import { ToastProvider } from './components/Toast'
import ProtectedRoute from './components/ProtectedRoute'
import ErrorBoundary from './components/ErrorBoundary'
import PageTransition from './components/PageTransition'

import LoginPage from './pages/LoginPage'
import NotFoundPage from './pages/NotFoundPage'
import { PORTAL_BASE } from './config/portalPaths'
import UpdateBanner from './components/UpdateBanner'
import { withChunkReload, reloadOnceForStaleChunk } from './utils/chunkReload'

// Lazy pages are wrapped so a missing chunk after a deploy (old tab → deleted hashed file)
// self-heals with a one-time reload instead of a permanent white-screen/ErrorBoundary crash.
const AgentConsole = lazy(withChunkReload(() => import('./pages/AgentConsole')))
const SetupWizard = lazy(withChunkReload(() => import('./pages/SetupWizard')))
const PartnerHandoffPage = lazy(withChunkReload(() => import('./pages/PartnerHandoffPage')))
const ChangePasswordPage = lazy(withChunkReload(() => import('./pages/ChangePasswordPage')))
const ResetPasswordPage = lazy(withChunkReload(() => import('./pages/ResetPasswordPage')))
const CsatPage = lazy(withChunkReload(() => import('./pages/CsatPage')))
const CustomerInvitationPage = lazy(withChunkReload(() => import('./pages/CustomerInvitationPage')))
const CustomerLoginPage = lazy(withChunkReload(() => import('./pages/CustomerLoginPage')))
const AdminOverview = lazy(withChunkReload(() => import('./pages/AdminOverview')))
const CreateTicketPage = lazy(withChunkReload(() => import('./pages/CreateTicketPage')))
const WorkflowAdmin = lazy(withChunkReload(() => import('./pages/WorkflowAdmin')))
const CustomerPortal = lazy(withChunkReload(() => import('./pages/CustomerPortal')))
const AgentEmailSettingsPage = lazy(withChunkReload(() => import('./pages/AgentEmailSettingsPage')))
const MaintenanceRecoveryPage = lazy(withChunkReload(() => import('./pages/MaintenanceRecoveryPage')))

// Vite fires this when a <link rel="modulepreload"> chunk fails to load (also a deploy/stale
// bundle symptom). Recover the same way.
if (typeof window !== 'undefined') {
  window.addEventListener('vite:preloadError', (e: any) => {
    if (reloadOnceForStaleChunk()) e?.preventDefault?.();
  });
}
const LazyFallback = () => (
  <div className="flex min-h-screen bg-gray-50 dark:bg-[#0a0a0f]">
    {/* Icon rail skeleton */}
    <div className="hidden sm:flex flex-col items-center w-14 border-r border-gray-200 dark:border-gray-800 py-4 gap-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="w-8 h-8 rounded-lg skeleton-shimmer bg-gray-200 dark:bg-gray-800" />
      ))}
    </div>
    {/* Sidebar skeleton */}
    <div className="hidden md:flex flex-col w-60 border-r border-gray-200 dark:border-gray-800 p-4 gap-4">
      <div className="h-5 w-24 rounded skeleton-shimmer bg-gray-200 dark:bg-gray-800" />
      <div className="h-4 w-40 rounded skeleton-shimmer bg-gray-200 dark:bg-gray-800" />
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="h-10 rounded-lg skeleton-shimmer bg-gray-200 dark:bg-gray-800" />
      ))}
    </div>
    {/* Main content skeleton */}
    <div className="flex-1 flex items-center justify-center">
      <div className="w-8 h-8 border-[3px] rounded-full animate-spin" style={{ borderColor: 'color-mix(in srgb, var(--color-primary, #EAB308) 30%, transparent)', borderTopColor: 'var(--color-primary, #EAB308)' }} />
    </div>
  </div>
)

const NavigateWithSearch = ({ to }: { to: string }) => {
  const location = useLocation()
  return <Navigate to={`${to}${location.search}`} replace />
}

/**
 * Die alte Ticketoberflaeche (`/tickets`, `/tickets/:id`) ist abgeschaltet: anderer
 * Look, kein Dark Mode, kein i18n -- ueber den "Zurueck"-Knopf und Asset-Links landete
 * man mitten in der Demo darin. Die Adressen bleiben erhalten und leiten in die
 * Konsole um, denn sie stehen in E-Mails, Lesezeichen und alten Verweisen.
 * Die Ticket-ID wird mitgenommen, damit der Verweis nicht nur "irgendwo" landet.
 */
const AltesTicketZurKonsole = () => {
  const { id } = useParams()
  const location = useLocation()
  return <Navigate to={legacyTicketRedirect(id, location.search)} replace />
}

function App() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const recoveryScope = useAuthStore((s) => s.user?.recovery_scope === true);

  // Load/reload app settings on startup and authentication changes
  useEffect(() => {
    let disposed = false, inFlight = false, lastAttempt = 0
    const store = useAppSettingsStore.getState()
    const refreshUser = async () => {
      if (disposed || !isAuthenticated || inFlight || Date.now() - lastAttempt < 30_000) return
      const before = useAuthStore.getState().user
      if (!before) return
      inFlight = true
      lastAttempt = Date.now()
      try {
        const response = await authApi.getCurrentUser()
        if (disposed || useAuthStore.getState().user !== before || !useAuthStore.getState().isAuthenticated) return
        const current = response.data
        if (String(current.id) !== String(before.id)) return
        useAuthStore.getState().setUser(current)
        const validPreset = findThemePreset(current.themePreset)?.id
        const validMode = ['light', 'dark', 'system'].includes(current.themeMode) ? current.themeMode : null
        store.setThemeOverride(validPreset || validMode
          ? { themePreset: validPreset as ThemePresetId | undefined, themeMode: validMode || undefined }
          : null)
      } catch {
        if (!disposed && useAuthStore.getState().user === before) store.setThemeOverride(null)
      } finally { inFlight = false }
    }
    if (!recoveryScope) void store.loadSettings()
    if (!isAuthenticated) store.setThemeOverride(null)
    else void refreshUser()
    const onFocus = () => { if (document.visibilityState === 'visible') void refreshUser() }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onFocus)
    const interval = window.setInterval(onFocus, 60_000)
    return () => {
      disposed = true
      window.clearInterval(interval)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onFocus)
    }
  }, [isAuthenticated, recoveryScope])

  // Session timeout & inactivity logout
  useSessionTimeout()

  return (
    <ErrorBoundary>
    <ToastProvider>
    {/* Die future-Flaggen v7_startTransition und v7_relativeSplatPath entfallen mit
        react-router 7 — dort ist beides das Standardverhalten. */}
    <BrowserRouter>
      <div className="min-h-screen w-full max-w-none bg-gray-50 dark:bg-[#0a0a0f] text-gray-900 dark:text-gray-100 transition-colors duration-200">
        <UpdateBanner />
        <Suspense fallback={<LazyFallback />}>
        <PageTransition>
        <Routes>
          <Route path="/public/vendor-escalation/:token" element={<PartnerHandoffPage />} />
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/maintenance-recovery" element={<ProtectedRoute allowedRoles={['admin']} allowRecovery><MaintenanceRecoveryPage /></ProtectedRoute>} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/setup" element={<ProtectedRoute allowedRoles={['admin']}><SetupWizard /></ProtectedRoute>} />
          <Route path="/tickets" element={<ProtectedRoute allowedRoles={['agent', 'admin']}><AltesTicketZurKonsole /></ProtectedRoute>} />
          <Route path="/tickets/new" element={<ProtectedRoute allowedRoles={['agent', 'admin']}><CreateTicketPage /></ProtectedRoute>} />
          <Route path="/tickets/:id" element={<ProtectedRoute allowedRoles={['agent', 'admin']}><AltesTicketZurKonsole /></ProtectedRoute>} />
          <Route path="/agent" element={<ProtectedRoute allowedRoles={['agent', 'admin']}><AgentConsole /></ProtectedRoute>} />
          <Route path="/calendar" element={<ProtectedRoute allowedRoles={['agent', 'admin']}><Navigate to="/agent" replace state={{ openCalendar: true }} /></ProtectedRoute>} />
          <Route path="/agent/email-settings" element={<ProtectedRoute allowedRoles={['agent', 'admin']}><AgentEmailSettingsPage /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin']}><AdminOverview /></ProtectedRoute>} />
          <Route path="/admin/workflows" element={<ProtectedRoute allowedRoles={['admin']}><WorkflowAdmin /></ProtectedRoute>} />
          <Route path="/admin/organization" element={<NavigateWithSearch to="/admin" />} />
          <Route path="/admin/email-settings" element={<NavigateWithSearch to="/admin" />} />
          <Route path="/admin/app-settings" element={<NavigateWithSearch to="/admin" />} />
          <Route path="/change-password" element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>} />
          <Route path="/csat/:token" element={<CsatPage />} />
          <Route path={`${PORTAL_BASE}/login`} element={<CustomerLoginPage />} />
          <Route path={`${PORTAL_BASE}/accept-invite`} element={<CustomerInvitationPage />} />
          <Route path={`${PORTAL_BASE}/reset-password`} element={<ResetPasswordPage customerPortal />} />
          <Route path={`${PORTAL_BASE}/*`} element={<ProtectedRoute requireCustomerAuth><CustomerPortal /></ProtectedRoute>} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </PageTransition>
        </Suspense>
      </div>
    </BrowserRouter>
    </ToastProvider>
    </ErrorBoundary>
  )
}

export default App
